// cubeSpawn.ts — the owner's easter egg. Click (mouse or pen) and drag
// anywhere on the page: loose cube faces spawn under the pointer, about one
// every 44px of drag, drifting gently where they land. Every six of them — always
// the six most recent, since the drag is continuous that is also "the six
// nearest" — pull together into one of the mark's own isometric cubes,
// which holds for 6s, fades over 600ms, and is gone.
//
// A plain CLICK, no drag, drops a single face where you clicked. That one
// face is the egg's only hint: the first outside viewers (15 Sep) never
// found a drag-anywhere egg unprompted, but everybody clicks on things.
// The page answering a stray click with the unit of the egg is the cue;
// curiosity supplies the drag. Six clicks inside the face's lifetime reach
// GROUP_SIZE and fold, the same as a drag would, so clicking is just the
// slow road to the same cube. Only a click on EMPTY GROUND drops one
// (owner's rule): nothing on copy (that is reading, not asking), nothing
// on anything painted over the layer -- a hero cube, the Latently room,
// the footer card, a glyph -- where the face would only appear behind it.
// A drag that starts anywhere still works as before.
//
// Mounted by: src/components/CubeSpawn.tsx, which renders nothing but the
// empty layer div and hands it here: `initCubeSpawn(layerEl)`. Everything
// else — document-level listeners, DOM creation for every loose face and
// cube, the one shared animation loop — lives in this file, the same
// "imperative motion module" shape markChoreography.ts and mintHandoff.ts
// already use, for the same reason: this drives dozens of short-lived DOM
// nodes at 60fps, which is not a job for React state.
//
// THE CAMERA. Every face and cube uses the exact CSS 3D recipe a cube built
// from six 64px div faces needs to read as the mark's own isometric cube:
// each face is `rotateX(-35.264deg) rotateY(-45deg)` (the cube's shared
// "camera") composed with its own local orientation (identity for the face
// pointing at the camera by default, rotateY/rotateX by 90/180 for the
// other five) — the true-isometric angle PAIR used in any CSS-cube demo,
// chosen because it is the angle CubeGlyph's own SVG projection (src/lib/
// iso.ts) is built to match. The pitch's SIGN is the one number this file
// does not take on faith: 35.264deg (positive, as first tried) shows the
// cube from underneath — the face that lands on top is "bottom", not
// "top" (checked with backface culling on, so only near faces showed;
// the glass panes no longer cull, but the camera is the same). -35.264deg is what
// actually lands top-up with front/right as the two lit sides, verified
// empirically (camera-variants.mjs, kept in the spawn scratchpad) rather
// than derived, since a sign flip this easy to get backwards is worth
// checking against the real renderer instead of hand algebra. No
// `perspective` is ever set: true isometric is orthographic (no vanishing
// point), matching iso.ts's flat SVG math, so this reads as the same
// object CubeGlyph draws, not a photographic version of it.
//
// THE GLASS. Every face is a pane of glass, and the glass is real in the
// one sense CSS can manage without WebGL: each pane bends what is actually
// behind it. CubeSpawn.css gives every face `backdrop-filter: url(#egg-
// glass-*)`, an SVG filter this module injects into the layer at mount
// (buildGlassDefs below): an feDisplacementMap driven by a LENS MAP (a
// 64px image where the red/green channels encode a displacement vector,
// zero in the middle of the pane, bending hard toward the centre in the
// last BEVEL_PX, the way a bevelled edge does), run three times at
// slightly different strengths for red, green and blue and recombined so
// the rim carries a hair of colour split, then softened and lifted. The
// backdrop is the page itself — the atmosphere, the other panes, the far
// faces of the same cube — so the far edges of a cube visibly refract
// through its near faces, which is what makes a glass box read as glass.
// Chromium honours url() filters on the backdrop; a browser that does not
// falls back (CubeSpawn.css declares the plain blur first) to a frosted
// pane, never to nothing. All six faces render (no backface culling any
// more): the owner's pick was the "glass box", far edges visible inside.
// The face's own paint on top of that is thin: a 10%-alpha tint in the
// mark's own gradient (applyFaceTint), a bright rim that fades inward
// (Fresnel, as inset shadows), one sharp reflection streak on top faces
// and a horizon band on side faces, and a 1px edge.
//
// LOOSE FACES carry the WHOLE camera baked into their own transform (no
// cube parent exists yet), plus a FIXED local counter-rotation that is
// exactly the camera's inverse — rotateY(45deg) rotateX(35.264deg) cancels
// rotateX(-35.264deg) rotateY(-45deg) (rotations around the same axis add,
// so Ry(-45)*Ry(45) = identity, then Rx(-35.264)*Rx(35.264) = identity) —
// so every loose face renders as a plain, undistorted, viewer-facing
// square while it drifts, whatever type it will become. Owner feedback:
// faces used to spawn PRE-rotated into their eventual slot orientation, so
// you saw three faces snap into a box, never six folding into one. Now
// the fold itself (formCube's flight, below) is what turns each face's
// LOCAL rotation from this viewer-facing start to its slot orientation,
// so all six panes are seen turning into place — the far three through
// the near three, since the panes are glass.
//
// FORMING builds a REAL `.cube` (transform-style: preserve-3d), but not
// the instant the sixth loose face lands: it spawns and drifts like any
// other for FOLD_DELAY_MS first (owner: "five tiles, sixth tile, beat,
// fold" — grouping in the same tick as the sixth spawn never let it be
// seen at all). The six children then start at a SCATTERED local position
// that reproduces, once the cube's own transform is applied, exactly the
// screen point each loose face was drifting at — solved by
// projectAxis/solveLocal below — so there is no seam, no dual rendering,
// and no separately-hand-rolled 3D math to keep in sync with what the
// browser is actually doing: the browser owns the 3D the whole time, this
// module only tells it where local (x, y, z) starts and ends. That solve
// has always treated the thing it is positioning as a "flat, camera-facing
// local square" (it never involved any face-specific rotation) — which is
// now exactly what a loose face IS, not just a stand-in approximation of
// one, so nothing about the position math needed to change.

import { prefersReducedMotion } from './reducedMotion';

declare global {
  interface Window {
    // Set true whenever the shared rAF loop is running, false the instant it
    // goes idle (nothing loose, nothing flying, nothing fading). Read-only
    // from the outside; exists purely so a test can observe "the loop is
    // idle" without reaching into module internals.
    __cubeSpawnActive?: boolean;
  }
}

/* ==========================================================================
   Tunables
   ========================================================================== */

const FACE_SIZE = 64; // px, both loose faces and cube-child faces
const FACE_HALF = FACE_SIZE / 2;
const TEXT_MARGIN = 24; // px of extra clearance kept off any text rect
const CLEARANCE = FACE_HALF + TEXT_MARGIN; // half-extent used for every push-clear check

// Faces spawn by DISTANCE dragged, not by the clock: a face every
// SPAWN_EVERY_PX of pointer travel, so a normal drag hands you six in under a
// second and they fold immediately, then the next six begin. Holding still
// spawns nothing. The small time floor stops a fast fling from dumping a
// dozen faces in one frame.
const SPAWN_EVERY_PX = 44;
const SPAWN_MIN_GAP_MS = 50;
const ACTIVATE_THRESHOLD_PX = 12;
const GROUP_SIZE = 6;
const MAX_LOOSE = 24;
const MAX_CUBES = 10; // a long drag leaves a trail; the oldest fades early

const FLIGHT_MS = 700;
const HOLD_MS = 6000;
// Faces left over from a drag that ended short of six fade out on their own
// after this long, so nothing lingers (and keeps the loop alive) forever.
// Two seconds is the owner's number ("give them 2s to live").
const STRAGGLER_MS = 2000;
// A face dropped by a plain click is the hint itself, so it gets a beat
// longer than a drag's leftovers: long enough to be seen drifting, short
// enough that a page nobody is touching goes still again.
const CLICK_FACE_MS = 3500;
const FADE_MS = 600;
const EVICT_FADE_MS = 250; // capped-out loose faces fade quickly, not held

const BOB_AMPLITUDE_PX = 6;
const BOB_PERIOD_MS = 2600;
const WOBBLE_DEG = 4;
const WOBBLE_PERIOD_MS = 3400;

// The lens map (see the header): how wide the bevel at a pane's rim is.
// How strong the pane's own tint is over the bent backdrop is the CSS's
// call, per theme (--egg-tint-pct, index.css).
const BEVEL_PX = 9;
// Per-channel displacement strengths, dark and light: the difference
// between them is the colour split at the rim. 2px in all on the dark
// ground reads as a hair of dispersion; on the light ground the same split
// prints an orange ghost beside every dark line, so light gets 0.6px.
const LENS_SCALE_DARK: [number, number, number] = [10, 11, 12];
const LENS_SCALE_LIGHT: [number, number, number] = [10, 10.3, 10.6];

// The three panes that end up at the back of a cube under this camera
// (back, left, bottom) sit dimmer, as a pane seen through two others
// would, so the box reads as depth rather than six equal frames. Set
// inline per child (not in CSS) because the cube's fade multiplies it:
// the fade runs on the six panes themselves, never on the .cube parent —
// opacity below 1 on the parent would force it flat (CSS's rule for
// opacity and transform-style) and the box would fall apart into six
// flat squares the instant it started to fade. The owner saw exactly that.
const FAR_FACE_OPACITY = 0.6;
const FAR_FACE_TYPES: FaceType[] = ['back', 'left', 'bottom'];

const CAMERA_PITCH = -35.264; // rotateX — fixed, the cube never pitches (see top comment: sign verified empirically)
const CAMERA_YAW = -45; // rotateY at rest
const CUBE_TURN_DEG = 14; // extra yaw a forming cube starts with, settling to 0

// A loose face's fixed local orientation: exactly the camera's inverse
// (rotateY(-CAMERA_YAW) rotateX(-CAMERA_PITCH)), so it renders flat and
// viewer-facing regardless of what type it will fold into. See top comment.
const FACE_FACING_ROTATE_Y = -CAMERA_YAW;
const FACE_FACING_ROTATE_X = -CAMERA_PITCH;
const FACE_FACING_LOCAL = `rotateY(${FACE_FACING_ROTATE_Y}deg) rotateX(${FACE_FACING_ROTATE_X}deg)`;

// How long a just-completed group of six sits as six ordinary loose faces
// (the sixth drifting like any other) before the fold actually starts —
// long enough that the sixth face is genuinely seen, not consumed the
// instant it spawns. Owner: "five tiles, sixth tile, beat, fold."
const FOLD_DELAY_MS = 320;

const DESKTOP_QUERY = '(min-width: 900px)';
const EXCLUDED_TARGETS = 'a, button, input, textarea, select, [contenteditable], .nav, .iconbtn';
const TEXT_SELECTOR = 'h1, h2, h3, p, li, a, button, .stamp, .livetag, .btn';
// Things that paint over the egg's layer and hit-test on their own: a click
// on one of these is not a click on empty ground. The exceptions are the
// SVGs that carry the mark's cubes -- the hero scene, the assembly's own
// mark, the mint cube in flight -- which are pointer-events: none so a
// parked or passing cube never eats a click, and so never show up as a
// press's target. They get their own hit test in pressedOnSceneFace.
const PAINTED_SELECTOR = 'svg, img, picture, video, canvas, .doorway, .foot';
const SCENE_SELECTOR = '.scene, .assembly__mark, .mint-flyer svg';

/* ==========================================================================
   Geometry: face types, camera math, the six slots
   ========================================================================== */

type FaceType = 'front' | 'right' | 'back' | 'left' | 'top' | 'bottom';
type Tone = 'mint' | 'ice' | 'peri' | 'lilac';

const FACE_ORDER: FaceType[] = ['front', 'right', 'back', 'left', 'top', 'bottom'];
const TONE_ORDER: Tone[] = ['mint', 'ice', 'peri', 'lilac'];

// The local orientation each face type needs at rest, on top of the shared
// camera — the standard six-div CSS cube recipe (rotateY/rotateX by 90 or
// 180; the front face needs no rotation of its own) — but kept as numbers,
// not a fixed CSS string, because the fold now INTERPOLATES a cube child's
// rotation from FACE_FACING_ROTATE_Y/X (viewer-facing, at spawn) to this
// slot value (at rest), frame by frame, rather than snapping to it.
const FACE_SLOT_ROTATE: Record<FaceType, { y: number; x: number }> = {
  front: { y: 0, x: 0 },
  back: { y: 180, x: 0 },
  right: { y: 90, x: 0 },
  left: { y: -90, x: 0 },
  top: { y: 0, x: 90 },
  bottom: { y: 0, x: -90 },
};

type Vec3 = { x: number; y: number; z: number };

// Each face's resting local position: the same rotation applied to
// (0, 0, FACE_HALF) — i.e. "push the flat face out along local Z, then
// turn it to face outward" — worked out by hand once here instead of at
// runtime, since the six results are fixed (textbook cube geometry).
const FACE_LOCAL_FINAL: Record<FaceType, Vec3> = {
  front: { x: 0, y: 0, z: FACE_HALF },
  back: { x: 0, y: 0, z: -FACE_HALF },
  right: { x: FACE_HALF, y: 0, z: 0 },
  left: { x: -FACE_HALF, y: 0, z: 0 },
  top: { x: 0, y: -FACE_HALF, z: 0 },
  bottom: { x: 0, y: FACE_HALF, z: 0 },
};

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function rotateAroundX(v: Vec3, deg: number): Vec3 {
  const c = Math.cos(toRad(deg));
  const s = Math.sin(toRad(deg));
  return { x: v.x, y: v.y * c - v.z * s, z: v.y * s + v.z * c };
}

function rotateAroundY(v: Vec3, deg: number): Vec3 {
  const c = Math.cos(toRad(deg));
  const s = Math.sin(toRad(deg));
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c };
}

// Where a LOCAL axis vector lands on screen once the camera's own
// `rotateX(pitch) rotateY(yaw)` is applied — matching CSS's own composition
// order (a transform list's leftmost function is outermost, so the
// rightmost — rotateY here — acts on the point first). Orthographic: no
// perspective is ever set, so the projection is just "drop z".
function projectAxis(v: Vec3, pitch: number, yaw: number): { x: number; y: number } {
  const afterYaw = rotateAroundY(v, yaw);
  const afterPitch = rotateAroundX(afterYaw, pitch);
  return { x: afterPitch.x, y: afterPitch.y };
}

// Solves lx*px + ly*py = (dx, dy) for lx, ly (Cramer's rule, z fixed at 0).
// Used once per forming cube: given a loose face's actual screen offset
// from the cube's target center, find the LOCAL (x, y, 0) that — once the
// cube's own camera transform is applied — reprojects to that same screen
// point. That local position becomes the child's flight starting point, so
// the child appears exactly where the loose face was, no swap, no seam.
function solveLocal(dx: number, dy: number, px: { x: number; y: number }, py: { x: number; y: number }): { x: number; y: number } {
  const det = px.x * py.y - py.x * px.y;
  if (Math.abs(det) < 1e-6) return { x: 0, y: 0 }; // camera degenerate; never happens for fixed angles
  return {
    x: (dx * py.y - py.x * dy) / det,
    y: (px.x * dy - dx * px.y) / det,
  };
}

/* ==========================================================================
   Appearance: the mark's own gradients, per face type and tone
   ========================================================================== */

// Copied verbatim from Scene.tsx's <Grad> stops (also mirrored in
// CubeGlyph.tsx for mint/ice/peri) — never invented fresh here. Same in
// both themes, as Scene.tsx's own gradients are.
const FACE_STOPS: Record<Tone, { top: [string, string]; right: [string, string]; left: [string, string] }> = {
  mint: { top: ['#ddf0f5', '#c2e2ec'], right: ['#bfe1ec', '#9ccbdc'], left: ['#a4cfdd', '#82b4c6'] },
  ice: { top: ['#f0f5fe', '#dfe9f9'], right: ['#d3e0f5', '#bccfef'], left: ['#c0cfea', '#a8bce0'] },
  peri: { top: ['#eef3fd', '#d3ddf5'], right: ['#c6d0ec', '#a9b7e0'], left: ['#aab8de', '#8c9cce'] },
  lilac: { top: ['#f6efff', '#e6d8fb'], right: ['#dccbf5', '#c8b3ea'], left: ['#cbb7ec', '#b09ad9'] },
};

// Multiplicative darken for the bottom face (never drawn by the SVG mark,
// since no camera angle here ever looks up at one) — derived from the same
// LEFT stops rather than inventing a new colour.
function darken(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * factor);
  const g = Math.round(((n >> 8) & 255) * factor);
  const b = Math.round((n & 255) * factor);
  return `rgb(${r}, ${g}, ${b})`;
}

// The pane's tint: the mark's own gradient stops for this face type,
// handed to CubeSpawn.css as custom properties (the CSS owns how much of
// the tint shows, per theme, and how it stacks with the rim). Gradient
// directions copied from Scene.tsx's own <Grad>: TOP stops use its
// non-vertical direction (~120deg as a CSS angle), RIGHT/LEFT stops run
// straight down.
function applyFaceTint(el: HTMLDivElement, type: FaceType, tone: Tone): void {
  const stops = FACE_STOPS[tone];
  let pair: [string, string];
  let angle = '180deg';
  if (type === 'top') {
    pair = stops.top;
    angle = '120deg';
  } else if (type === 'front' || type === 'left') {
    pair = stops.left;
  } else if (type === 'right' || type === 'back') {
    pair = stops.right;
  } else {
    pair = [darken(stops.left[0], 0.82), darken(stops.left[1], 0.82)];
  }
  el.style.setProperty('--egg-tint-a', pair[0]);
  el.style.setProperty('--egg-tint-b', pair[1]);
  el.style.setProperty('--egg-tint-angle', angle);
}

/* ==========================================================================
   The lens: an SVG filter on the backdrop (see the header)
   ========================================================================== */

// A FACE_SIZE-square image whose red and green channels encode, per pixel,
// the displacement feDisplacementMap should apply (0.5 = none). Zero in the
// middle of the pane; in the last BEVEL_PX of any edge it points toward the
// centre with a strength rising as the square of how deep into the bevel
// the pixel is, so the bend is soft where it starts and hard at the rim.
function lensMapDataUrl(): string | null {
  const c = document.createElement('canvas');
  c.width = FACE_SIZE;
  c.height = FACE_SIZE;
  const g = c.getContext('2d');
  if (!g) return null;
  const img = g.createImageData(FACE_SIZE, FACE_SIZE);
  for (let y = 0; y < FACE_SIZE; y++) {
    for (let x = 0; x < FACE_SIZE; x++) {
      const dx = x + 0.5 - FACE_HALF;
      const dy = y + 0.5 - FACE_HALF;
      const edge = Math.min(x + 0.5, y + 0.5, FACE_SIZE - x - 0.5, FACE_SIZE - y - 0.5);
      let strength = 0;
      if (edge < BEVEL_PX) {
        const u = 1 - edge / BEVEL_PX;
        strength = u * u;
      }
      const len = Math.hypot(dx, dy) || 1;
      const i = (y * FACE_SIZE + x) * 4;
      img.data[i] = Math.round(127.5 + (-dx / len) * strength * 127.5);
      img.data[i + 1] = Math.round(127.5 + (-dy / len) * strength * 127.5);
      img.data[i + 2] = 0;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return c.toDataURL('image/png');
}

// The two filters CubeSpawn.css references, one per theme (the dark one
// lifts and saturates the backdrop a little, as light through glass on a
// dark ground reads; the light one only bends and softens). Each: three
// displacements, one per channel at that theme's LENS_SCALE_*, recombined;
// a soft blur; the lift. Returned as an <svg> to drop into the layer, or null if a canvas
// is unavailable (then the CSS fallback blur is all there is).
function buildGlassDefs(): SVGSVGElement | null {
  const map = lensMapDataUrl();
  if (!map) return null;
  const channel = (name: 'r' | 'g' | 'b', input: string) => {
    const row = name === 'r' ? '1 0 0 0 0  0 0 0 0 0  0 0 0 0 0' : name === 'g' ? '0 0 0 0 0  0 1 0 0 0  0 0 0 0 0' : '0 0 0 0 0  0 0 0 0 0  0 0 1 0 0';
    return `<feColorMatrix in="${input}" type="matrix" values="${row}  0 0 0 1 0" result="${name}"/>`;
  };
  const filter = (id: string, scales: [number, number, number], lift: number, saturate: number) => `
    <filter id="${id}" x="0" y="0" width="1" height="1" color-interpolation-filters="sRGB">
      <feImage href="${map}" result="map" preserveAspectRatio="none" x="0" y="0" width="100%" height="100%"/>
      <feDisplacementMap in="SourceGraphic" in2="map" scale="${scales[0]}" xChannelSelector="R" yChannelSelector="G" result="dr"/>
      <feDisplacementMap in="SourceGraphic" in2="map" scale="${scales[1]}" xChannelSelector="R" yChannelSelector="G" result="dg"/>
      <feDisplacementMap in="SourceGraphic" in2="map" scale="${scales[2]}" xChannelSelector="R" yChannelSelector="G" result="db"/>
      ${channel('r', 'dr')}${channel('g', 'dg')}${channel('b', 'db')}
      <feComposite in="r" in2="g" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="rg"/>
      <feComposite in="rg" in2="b" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="rgb"/>
      <feGaussianBlur in="rgb" stdDeviation="0.6" result="soft"/>
      <feColorMatrix in="soft" type="saturate" values="${saturate}" result="sat"/>
      <feComponentTransfer in="sat">
        <feFuncR type="linear" slope="${lift}"/><feFuncG type="linear" slope="${lift}"/><feFuncB type="linear" slope="${lift}"/>
      </feComponentTransfer>
    </filter>`;
  const host = document.createElement('div');
  host.innerHTML = `<svg class="cube-spawn__defs" width="0" height="0" aria-hidden="true" focusable="false">${filter('egg-glass-dark', LENS_SCALE_DARK, 1.1, 1.2)}${filter('egg-glass-light', LENS_SCALE_LIGHT, 1.0, 1.1)}</svg>`;
  return host.firstElementChild as SVGSVGElement | null;
}

/* ==========================================================================
   Small math helpers
   ========================================================================== */

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Ease-out-back, capped at 6% overshoot, one settle — hand-written, no
// library. Standard cubic back-out: f(x) = 1 + c3*x^3 + c1*x^2 for
// x = t-1 in [-1, 0], c3 = c1+1 (the default c1 = 1.70158 overshoots 10%).
// Its peak overshoot works out to 4*c1^3 / (27*(c1+1)^2); solving that for
// a 0.06 peak gives c1 ~= 1.28.
const BACK_C1 = 1.28;
const BACK_C3 = BACK_C1 + 1;
function easeOutBack(t: number): number {
  const x = t - 1;
  return 1 + BACK_C3 * x * x * x + BACK_C1 * x * x;
}

/* ==========================================================================
   Text-avoidance
   ========================================================================== */

interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// If the (x, y)-centered box of the given half-extent overlaps rect, the
// minimum push that clears it along whichever axis is cheaper. Null if
// there is no overlap.
function pushClear(x: number, y: number, half: number, rect: Rect): { x: number; y: number } | null {
  const left = x - half;
  const right = x + half;
  const top = y - half;
  const bottom = y + half;
  const overlapX = Math.min(right, rect.right) - Math.max(left, rect.left);
  const overlapY = Math.min(bottom, rect.bottom) - Math.max(top, rect.top);
  if (overlapX <= 0 || overlapY <= 0) return null;
  if (overlapX < overlapY) {
    const dir = x < (rect.left + rect.right) / 2 ? -1 : 1;
    return { x: x + dir * overlapX, y };
  }
  const dir = y < (rect.top + rect.bottom) / 2 ? -1 : 1;
  return { x, y: y + dir * overlapY };
}

/* ==========================================================================
   Per-instance state
   ========================================================================== */

interface LooseFace {
  el: HTMLDivElement;
  type: FaceType;
  tone: Tone;
  x: number; // spawn point, document coords
  y: number;
  bobPhase: number;
  wobblePhase: number;
  fadeStart: number | null; // set only when evicted by the MAX_LOOSE cap
}

interface CubeChild {
  el: HTMLDivElement;
  type: FaceType;
  baseOpacity: number; // 1, or FAR_FACE_OPACITY for the three back panes
  start: Vec3; // local offset at flight start (scattered, z always 0)
  final: Vec3; // local offset at rest — FACE_LOCAL_FINAL[type]
}

interface CubeState {
  el: HTMLDivElement;
  children: CubeChild[];
  centerX: number; // document coords, fixed for the cube's whole life
  centerY: number;
  flightStart: number;
  flying: boolean;
  fading: boolean;
  fadeStart: number | null;
  holdTimer: number | null;
}

export function initCubeSpawn(layer: HTMLDivElement): () => void {
  // Item 9: under reduced motion or below the desktop breakpoint, attach
  // nothing at all — the layer stays empty and inert.
  const desktopQuery = window.matchMedia(DESKTOP_QUERY);
  if (prefersReducedMotion() || !desktopQuery.matches) {
    return () => {};
  }

  let loose: LooseFace[] = [];
  let cubes: CubeState[] = [];
  let spawnCount = 0;
  let textRects: Rect[] = [];
  let raf = 0;
  // Pending fold: set the moment loose reaches six, cleared once formCube()
  // actually runs (or on unmount). Guards against scheduling twice if a
  // seventh face lands before the delay is up -- the fold still takes the
  // first six, in spawn order (see formCube).
  let foldTimer: number | null = null;

  interface DragState {
    pointerId: number;
    startX: number; // client coords, for the activation threshold only
    startY: number;
    active: boolean;
    onContent: boolean; // the press landed on copy or paint, not empty ground
    lastDocX: number; // document coords, kept live by pointermove
    lastDocY: number;
  lastSpawnDocX: number;
  lastSpawnDocY: number;
  lastSpawnAt: number;
  }
  let drag: DragState | null = null;

  // Whether a press landed on words. TEXT_SELECTOR covers the page's own
  // copy elements; the text-node check catches the odd span or div that
  // holds words directly (nav wordmark, footer lines, the hero note).
  function pressedOnText(target: Element | null): boolean {
    if (!target) return false;
    if (target.closest(TEXT_SELECTOR)) return true;
    for (const n of target.childNodes) {
      if (n.nodeType === Node.TEXT_NODE && n.textContent?.trim()) return true;
    }
    return false;
  }

  // Whether a press landed on one of the mark's cube faces. Those SVGs are
  // pointer-events: none, so the press's own target is whatever sits under
  // them; for one hit test they are made hit-testable again and the
  // browser answers with its real geometry, transforms and all. Only a
  // face with real opacity counts: the dashed cube's faint top plane
  // (0.14) is empty ground, since a face dropped inside the unfinished
  // cube shows through.
  function pressedOnSceneFace(clientX: number, clientY: number): boolean {
    const scenes = document.querySelectorAll<SVGElement>(SCENE_SELECTOR);
    if (scenes.length === 0) return false;
    scenes.forEach((s) => (s.style.pointerEvents = 'auto'));
    const hit = document.elementFromPoint(clientX, clientY);
    scenes.forEach((s) => (s.style.pointerEvents = ''));
    if (!(hit instanceof SVGGeometryElement) || !hit.closest(SCENE_SELECTOR)) return false;
    return parseFloat(getComputedStyle(hit).opacity) > 0.5;
  }

  function pressedOnContent(target: Element | null, clientX: number, clientY: number): boolean {
    if (pressedOnText(target)) return true;
    if (target?.closest(PAINTED_SELECTOR)) return true;
    return pressedOnSceneFace(clientX, clientY);
  }

  function collectTextRects(): void {
    const rects: Rect[] = [];
    document.querySelectorAll<HTMLElement>(TEXT_SELECTOR).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      if (getComputedStyle(el).visibility === 'hidden') return;
      rects.push({
        left: r.left + window.scrollX,
        right: r.right + window.scrollX,
        top: r.top + window.scrollY,
        bottom: r.bottom + window.scrollY,
      });
    });
    textRects = rects;
  }

  // Pushes (x, y) clear of every cached text rect, iterating a handful of
  // passes so being pushed out of one rect into another still converges.
  function resolveSpawnPoint(x: number, y: number): { x: number; y: number } {
    let px = x;
    let py = y;
    for (let pass = 0; pass < 8; pass++) {
      let moved = false;
      for (const rect of textRects) {
        const pushed = pushClear(px, py, CLEARANCE, rect);
        if (pushed) {
          px = pushed.x;
          py = pushed.y;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return { x: px, y: py };
  }

  function ensureLoopRunning(): void {
    if (raf) return;
    raf = requestAnimationFrame(tick);
    window.__cubeSpawnActive = true;
  }

  function evictOldestLoose(): void {
    const oldest = loose.find((f) => f.fadeStart === null);
    if (oldest) oldest.fadeStart = performance.now();
  }

  function spawnFace(rawX: number, rawY: number): void {
    const { x, y } = resolveSpawnPoint(rawX, rawY);
    if (loose.filter((f) => f.fadeStart === null).length >= MAX_LOOSE) evictOldestLoose();

    const index = spawnCount++;
    const type = FACE_ORDER[index % FACE_ORDER.length];
    const tone = TONE_ORDER[Math.floor(index / GROUP_SIZE) % TONE_ORDER.length];

    const el = document.createElement('div');
    el.className = `cube-face cube-face--${type}`;
    applyFaceTint(el, type, tone);
    layer.appendChild(el);

    loose.push({
      el,
      type,
      tone,
      x,
      y,
      bobPhase: (index % 5) * 1.25,
      wobblePhase: (index % 7) * 0.9,
      fadeStart: null,
    });

    // Reaching six schedules the fold rather than triggering it in this
    // same tick: forming synchronously here meant the sixth face was
    // spliced back out of `loose` before a single frame ever rendered it,
    // so a drag looked like "five faces, then a cube" -- the sixth was
    // never actually seen. Waiting FOLD_DELAY_MS lets it spawn and drift
    // like the other five first. Guarded by the null check so a seventh
    // spawn (see spawnAt/onPointerMove) doesn't reschedule -- the fold
    // still resolves to the first six, in spawn order.
    if (loose.length >= GROUP_SIZE && foldTimer === null) {
      foldTimer = window.setTimeout(() => {
        foldTimer = null;
        formCube();
      }, FOLD_DELAY_MS);
    }
    ensureLoopRunning();
  }

  function formCube(): void {
    const faces = loose.splice(0, GROUP_SIZE);
    let sumX = 0;
    let sumY = 0;
    for (const f of faces) {
      sumX += f.x;
      sumY += f.y;
    }
    const center = resolveSpawnPoint(sumX / GROUP_SIZE, sumY / GROUP_SIZE);

    if (cubes.filter((c) => !c.fading).length >= MAX_CUBES) {
      const oldest = cubes.find((c) => !c.fading);
      if (oldest) startFade(oldest);
    }

    const cubeEl = document.createElement('div');
    cubeEl.className = 'cube';
    layer.appendChild(cubeEl);

    // The camera the SCATTERED start has to match: the turned yaw a
    // forming cube starts at, not the resting one (the child's local
    // offset is fixed for the whole flight; only the camera around it
    // changes as the turn settles).
    const px = projectAxis({ x: 1, y: 0, z: 0 }, CAMERA_PITCH, CAMERA_YAW + CUBE_TURN_DEG);
    const py = projectAxis({ x: 0, y: 1, z: 0 }, CAMERA_PITCH, CAMERA_YAW + CUBE_TURN_DEG);

    const children: CubeChild[] = faces.map((f) => {
      const el = document.createElement('div');
      el.className = `cube-face cube-face--${f.type}`;
      applyFaceTint(el, f.type, f.tone);
      const baseOpacity = FAR_FACE_TYPES.includes(f.type) ? FAR_FACE_OPACITY : 1;
      el.style.opacity = String(baseOpacity);
      cubeEl.appendChild(el);
      const local = solveLocal(f.x - center.x, f.y - center.y, px, py);
      return {
        el,
        type: f.type,
        baseOpacity,
        start: { x: local.x, y: local.y, z: 0 },
        final: FACE_LOCAL_FINAL[f.type],
      };
    });

    for (const f of faces) f.el.remove();

    cubes.push({
      el: cubeEl,
      children,
      centerX: center.x,
      centerY: center.y,
      flightStart: performance.now(),
      flying: true,
      fading: false,
      fadeStart: null,
      holdTimer: null,
    });
    ensureLoopRunning();
  }

  function startFade(cube: CubeState): void {
    if (cube.fading) return;
    if (cube.holdTimer !== null) {
      window.clearTimeout(cube.holdTimer);
      cube.holdTimer = null;
    }
    cube.fading = true;
    cube.fadeStart = performance.now();
    ensureLoopRunning();
  }

  function setFaceTransform(el: HTMLDivElement, cx: number, cy: number, cz: number, rotateExtra: string, local: string): void {
    el.style.transform =
      `translate3d(${(cx - FACE_HALF).toFixed(1)}px, ${(cy - FACE_HALF).toFixed(1)}px, ${cz.toFixed(1)}px) ` +
      `${rotateExtra}${local}`;
  }

  function tick(now: number): void {
    raf = 0;
    if (!drag?.active && stragglersFadeAt !== null && now > stragglersFadeAt) {
      for (const f of loose) if (f.fadeStart === null) f.fadeStart = now;
      stragglersFadeAt = null;
    }
    const rect = layer.getBoundingClientRect();
    const originX = rect.left + window.scrollX;
    const originY = rect.top + window.scrollY;

    for (const f of loose) {
      if (f.fadeStart !== null) {
        const p = clamp01((now - f.fadeStart) / EVICT_FADE_MS);
        f.el.style.opacity = String(1 - p);
        continue; // evicted faces hold their last drift position while they fade
      }
      const bob = Math.sin(now / BOB_PERIOD_MS + f.bobPhase) * BOB_AMPLITUDE_PX;
      const wobble = Math.sin(now / WOBBLE_PERIOD_MS + f.wobblePhase) * WOBBLE_DEG;
      const screenX = f.x - originX;
      const screenY = f.y - originY + bob;
      // FACE_FACING_LOCAL, not a per-type slot rotation: every loose face
      // renders as the same flat, viewer-facing square regardless of which
      // type it will fold into (see top comment) -- only its colour hints
      // at what it will become.
      setFaceTransform(
        f.el,
        screenX,
        screenY,
        0,
        `rotateZ(${wobble.toFixed(2)}deg) rotateX(${CAMERA_PITCH}deg) rotateY(${CAMERA_YAW}deg) `,
        FACE_FACING_LOCAL,
      );
    }
    for (let i = loose.length - 1; i >= 0; i--) {
      const f = loose[i];
      if (f.fadeStart !== null && now - f.fadeStart >= EVICT_FADE_MS) {
        f.el.remove();
        loose.splice(i, 1);
      }
    }

    for (const cube of cubes) {
      if (cube.flying) {
        const t = clamp01((now - cube.flightStart) / FLIGHT_MS);
        const done = t >= 1;
        const eased = done ? 1 : easeOutBack(t);
        const turn = done ? 0 : CUBE_TURN_DEG * (1 - t) * (1 - t);
        const cx = cube.centerX - originX;
        const cy = cube.centerY - originY;
        cube.el.style.transform =
          `translate3d(${(cx - FACE_HALF).toFixed(1)}px, ${(cy - FACE_HALF).toFixed(1)}px, 0) ` +
          `rotateX(${CAMERA_PITCH}deg) rotateY(${(CAMERA_YAW + turn).toFixed(2)}deg)`;
        for (const child of cube.children) {
          const lx = lerp(child.start.x, child.final.x, eased);
          const ly = lerp(child.start.y, child.final.y, eased);
          const lz = lerp(child.start.z, child.final.z, eased);
          // Rotation interpolates too, from the viewer-facing start every
          // loose face spawned with to this child's slot orientation --
          // this (not the position flight alone) is what makes six faces
          // visibly fold into a box: every pane is seen turning from flat
          // to its slot, the far three through the near three (they are
          // glass), instead of having already been in their final
          // orientation from the first frame.
          const slot = FACE_SLOT_ROTATE[child.type];
          const rotY = lerp(FACE_FACING_ROTATE_Y, slot.y, eased);
          const rotX = lerp(FACE_FACING_ROTATE_X, slot.x, eased);
          child.el.style.transform =
            `translate3d(${(lx - FACE_HALF).toFixed(1)}px, ${(ly - FACE_HALF).toFixed(1)}px, ${lz.toFixed(1)}px) ` +
            `rotateY(${rotY.toFixed(2)}deg) rotateX(${rotX.toFixed(2)}deg)`;
        }
        if (done) {
          cube.flying = false;
          cube.holdTimer = window.setTimeout(() => startFade(cube), HOLD_MS);
        }
      } else if (cube.fading && cube.fadeStart !== null) {
        // Fade the six panes, not the .cube: see FAR_FACE_OPACITY's comment.
        const p = clamp01((now - cube.fadeStart) / FADE_MS);
        for (const child of cube.children) child.el.style.opacity = String(child.baseOpacity * (1 - p));
      }
    }
    for (let i = cubes.length - 1; i >= 0; i--) {
      const cube = cubes[i];
      if (cube.fading && cube.fadeStart !== null && now - cube.fadeStart >= FADE_MS) {
        cube.el.remove();
        cubes.splice(i, 1);
      }
    }

    const stillActive = loose.length > 0 || cubes.some((c) => c.flying || c.fading);
    if (stillActive) {
      raf = requestAnimationFrame(tick);
    } else {
      window.__cubeSpawnActive = false;
    }
  }

  function activateDrag(): void {
    if (!drag) return;
    drag.active = true;
    document.documentElement.classList.add('is-dragging-cubes');
    window.getSelection()?.removeAllRanges();
    collectTextRects();
    spawnAt(drag.lastDocX, drag.lastDocY);
  }

  function spawnAt(docX: number, docY: number): void {
    if (!drag) return;
    spawnFace(docX, docY);
    drag.lastSpawnDocX = docX;
    drag.lastSpawnDocY = docY;
    drag.lastSpawnAt = performance.now();
  }

  // When the loose faces now on the page should start fading, as an absolute
  // time: STRAGGLER_MS after a drag ends, CLICK_FACE_MS after a click. One
  // deadline for all of them, pushed back by every new press, so a run of
  // quick clicks keeps its faces alive long enough to fold.
  let stragglersFadeAt: number | null = null;

  function deactivateDrag(): void {
    stragglersFadeAt = performance.now() + STRAGGLER_MS;
    if (drag?.active) document.documentElement.classList.remove('is-dragging-cubes');
  }

  // The click: a press that ended before ever crossing ACTIVATE_THRESHOLD_PX.
  // Same spawn as a drag's (text avoidance included, so the rects are
  // collected first), just one face and no drag state -- no selection
  // clearing, no is-dragging-cubes, since nothing is being dragged.
  function dropClickFace(docX: number, docY: number): void {
    collectTextRects();
    spawnFace(docX, docY);
    stragglersFadeAt = performance.now() + CLICK_FACE_MS;
  }

  function onPointerDown(e: PointerEvent): void {
    if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    if (e.button !== 0 || !e.isPrimary) return;
    if (!desktopQuery.matches || prefersReducedMotion()) return;
    const target = e.target as Element | null;
    if (target?.closest(EXCLUDED_TARGETS)) return;
    drag = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      active: false,
      onContent: pressedOnContent(target, e.clientX, e.clientY),
      lastDocX: e.pageX,
      lastDocY: e.pageY,
      lastSpawnDocX: e.pageX,
      lastSpawnDocY: e.pageY,
      lastSpawnAt: 0,
    };
  }

  function onPointerMove(e: PointerEvent): void {
    if (!drag || e.pointerId !== drag.pointerId) return;
    drag.lastDocX = e.pageX;
    drag.lastDocY = e.pageY;
    if (drag.active) {
      const sx = e.pageX - drag.lastSpawnDocX;
      const sy = e.pageY - drag.lastSpawnDocY;
      const farEnough = sx * sx + sy * sy >= SPAWN_EVERY_PX * SPAWN_EVERY_PX;
      const notTooSoon = performance.now() - drag.lastSpawnAt >= SPAWN_MIN_GAP_MS;
      if (farEnough && notTooSoon) spawnAt(e.pageX, e.pageY);
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (dx * dx + dy * dy < ACTIVATE_THRESHOLD_PX * ACTIVATE_THRESHOLD_PX) return;
    activateDrag();
  }

  function onPointerEnd(e: PointerEvent): void {
    if (!drag || e.pointerId !== drag.pointerId) return;
    if (drag.active || e.type === 'pointercancel') {
      deactivateDrag();
    } else if (!drag.onContent) {
      dropClickFace(e.pageX, e.pageY);
    }
    drag = null;
  }

  function onResize(): void {
    collectTextRects();
  }

  // The glass filters, once per layer (see the header). Appended before any
  // face exists so the first pane already has its lens.
  const glassDefs = buildGlassDefs();
  if (glassDefs) layer.appendChild(glassDefs);

  document.addEventListener('pointerdown', onPointerDown, { passive: true });
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerup', onPointerEnd, { passive: true });
  document.addEventListener('pointercancel', onPointerEnd, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  return () => {
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerEnd);
    document.removeEventListener('pointercancel', onPointerEnd);
    window.removeEventListener('resize', onResize);
    document.documentElement.classList.remove('is-dragging-cubes');
    if (raf) cancelAnimationFrame(raf);
    if (foldTimer !== null) window.clearTimeout(foldTimer);
    for (const f of loose) f.el.remove();
    loose = [];
    for (const cube of cubes) {
      if (cube.holdTimer !== null) window.clearTimeout(cube.holdTimer);
      cube.el.remove();
    }
    cubes = [];
    drag = null;
    glassDefs?.remove();
    window.__cubeSpawnActive = false;
  };
}
